import { observable } from '@legendapp/state';
import { syncObservable } from '@legendapp/state/sync';
import { ObservablePersistLocalStorage } from '@legendapp/state/persist-plugins/local-storage';
import { supabase } from '@/lib/supabaseClient';
import { auth$ } from './auth$';

// Cache of tag paths that the current user has shared (as owner)
export const sharedTagPaths$ = observable([]);

// Cache of tags shared with the current user (as member)
// Shape: [{ id, tag_path, owner_id, owner_email, owner_username, owner_avatar_url }]
export const sharedWithMe$ = observable([]);

// Persist to localStorage for instant access on page load
syncObservable(sharedTagPaths$, {
  persist: {
    name: 'promptnote-shared-tag-paths',
    plugin: ObservablePersistLocalStorage,
  },
});

syncObservable(sharedWithMe$, {
  persist: {
    name: 'promptnote-shared-with-me',
    plugin: ObservablePersistLocalStorage,
  },
});

/**
 * Refresh the shared tags cache
 * Fetches tags the user owns (shared) and tags shared with them (as member)
 */
export const refreshSharedTagsCache = async () => {
  const user = auth$.user.get();
  if (!user) return;

  // Fetch tags I own (shared with others)
  const { data: ownedTags, error: ownedError } = await supabase
    .from('promptnote_shared_tags')
    .select('id, tag_path, owner_id, created_at')
    .eq('owner_id', user.id);

  if (ownedError) {
    console.error('[sharedTags$] Failed to fetch owned shared tags:', ownedError);
  } else {
    sharedTagPaths$.set(ownedTags || []);
  }

  // Fetch tags shared with me (I'm a member)
  const { data: memberOf, error: memberError } = await supabase
    .from('promptnote_shared_tag_members')
    .select('shared_tag_id, promptnote_shared_tags(id, tag_path, owner_id)')
    .eq('user_id', user.id);

  if (memberError) {
    console.error('[sharedTags$] Failed to fetch shared-with-me tags:', memberError);
  } else {
    // For each shared tag, get owner info
    const sharedWithMeData = [];
    for (const row of (memberOf || [])) {
      const st = row.promptnote_shared_tags;
      if (!st) continue;

      // Get owner metadata via RPC
      const ownerInfo = await getOwnerInfo(st.owner_id);
      sharedWithMeData.push({
        id: st.id,
        tag_path: st.tag_path,
        owner_id: st.owner_id,
        owner_email: ownerInfo.email,
        owner_username: ownerInfo.username,
        owner_avatar_url: ownerInfo.avatar_url,
      });
    }
    sharedWithMe$.set(sharedWithMeData);
  }

  console.log('[sharedTags$] Cache refreshed:', {
    owned: sharedTagPaths$.get()?.length || 0,
    sharedWithMe: sharedWithMe$.get()?.length || 0,
  });
};

/**
 * Get user public info by ID
 */
const getOwnerInfo = async (ownerId) => {
  const { data, error } = await supabase.rpc('get_user_public_info', { p_user_id: ownerId });
  if (error || !data || data.length === 0) {
    return { email: '', username: 'User', avatar_url: null };
  }
  const user = data[0];
  return {
    email: user.email,
    username: user.raw_user_meta_data?.username || user.raw_user_meta_data?.name || user.email?.split('@')[0] || 'User',
    avatar_url: user.raw_user_meta_data?.avatar_url || null,
  };
};

/**
 * Check if a tag path is shared by the current user (synchronous, from cache)
 */
export const isTagShared = (tagPath) => {
  const owned = sharedTagPaths$.get() || [];
  return owned.some(t => t.tag_path === tagPath);
};

/**
 * Get the shared tag record for a path owned by current user
 */
export const getSharedTagForPath = (tagPath) => {
  const owned = sharedTagPaths$.get() || [];
  return owned.find(t => t.tag_path === tagPath) || null;
};

/**
 * Find a user by email
 */
export const findUserByEmail = async (email) => {
  const { data, error } = await supabase.rpc('find_user_by_email', { p_email: email });
  if (error || !data || data.length === 0) return null;

  const user = data[0];
  return {
    id: user.id,
    email: user.email,
    username: user.raw_user_meta_data?.username || user.raw_user_meta_data?.name || user.email.split('@')[0],
    avatar_url: user.raw_user_meta_data?.avatar_url || null,
  };
};

/**
 * Get all members of a shared tag
 */
export const getSharedTagMembers = async (sharedTagId) => {
  const { data, error } = await supabase.rpc('get_shared_tag_members', { p_shared_tag_id: sharedTagId });
  if (error) {
    console.error('[sharedTags$] Failed to get members:', error);
    return [];
  }

  return (data || []).map(m => ({
    id: m.id,
    user_id: m.user_id,
    email: m.email,
    username: m.user_meta?.username || m.user_meta?.name || m.email?.split('@')[0] || 'User',
    avatar_url: m.user_meta?.avatar_url || null,
    created_at: m.created_at,
  }));
};

/**
 * Get notes for a shared tag (auto-merge: all members' notes)
 */
export const getSharedTagNotes = async (sharedTagId) => {
  const user = auth$.user.get();
  if (!user) return [];

  const { data, error } = await supabase.rpc('get_shared_tag_notes', {
    p_shared_tag_id: sharedTagId,
    p_user_id: user.id,
  });

  if (error) {
    console.error('[sharedTags$] Failed to get shared notes:', error);
    return [];
  }

  return (data || []).map(n => ({
    id: n.note_id,
    title: n.note_title,
    tags: n.note_tags,
    created_at: n.note_created_at,
    updated_at: n.note_updated_at,
    user_id: n.note_user_id,
    pinned: n.note_pinned,
    author_email: n.author_email,
    author_username: n.author_meta?.username || n.author_meta?.name || n.author_email?.split('@')[0] || 'User',
    author_avatar_url: n.author_meta?.avatar_url || null,
    snippets: n.snippets || [],
    isOwn: n.note_user_id === user.id,
  }));
};

/**
 * Share a tag (create shared tag record)
 */
export const shareTag = async (tagPath) => {
  const user = auth$.user.get();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('promptnote_shared_tags')
    .insert({ tag_path: tagPath, owner_id: user.id })
    .select('id, tag_path, owner_id, created_at')
    .single();

  if (error) throw error;

  // Update cache
  const current = sharedTagPaths$.get() || [];
  sharedTagPaths$.set([...current, data]);

  return data;
};

/**
 * Stop sharing a tag (delete shared tag — cascades members)
 */
export const stopSharing = async (sharedTagId) => {
  const { error } = await supabase
    .from('promptnote_shared_tags')
    .delete()
    .eq('id', sharedTagId);

  if (error) throw error;

  // Update cache
  const current = sharedTagPaths$.get() || [];
  sharedTagPaths$.set(current.filter(t => t.id !== sharedTagId));
};

/**
 * Invite a member to a shared tag
 */
export const inviteMember = async (sharedTagId, userId) => {
  const currentUserId = auth$.user.get()?.id;

  if (userId === currentUserId) {
    return { success: false, error: 'Cannot invite yourself' };
  }

  // Check if already a member
  const members = await getSharedTagMembers(sharedTagId);
  if (members.some(m => m.user_id === userId)) {
    return { success: false, error: 'Already a member' };
  }

  const { error } = await supabase
    .from('promptnote_shared_tag_members')
    .insert({ shared_tag_id: sharedTagId, user_id: userId });

  if (error) {
    console.error('[sharedTags$] Failed to invite member:', error);
    return { success: false, error: error.message };
  }

  return { success: true };
};

/**
 * Remove a member from a shared tag (owner action)
 */
export const removeMember = async (sharedTagId, userId) => {
  const { error } = await supabase
    .from('promptnote_shared_tag_members')
    .delete()
    .eq('shared_tag_id', sharedTagId)
    .eq('user_id', userId);

  if (error) {
    console.error('[sharedTags$] Failed to remove member:', error);
    return { success: false, error: error.message };
  }

  return { success: true };
};

/**
 * Leave a shared tag (member action — removes self)
 */
export const leaveSharedTag = async (sharedTagId) => {
  const userId = auth$.user.get()?.id;
  if (!userId) return { success: false, error: 'Not authenticated' };

  const { error } = await supabase
    .from('promptnote_shared_tag_members')
    .delete()
    .eq('shared_tag_id', sharedTagId)
    .eq('user_id', userId);

  if (error) {
    console.error('[sharedTags$] Failed to leave:', error);
    return { success: false, error: error.message };
  }

  // Update cache
  const current = sharedWithMe$.get() || [];
  sharedWithMe$.set(current.filter(t => t.id !== sharedTagId));

  return { success: true };
};

/**
 * Update shared tag path (when owner renames a tag)
 */
export const updateSharedTagPath = async (oldPath, newPath) => {
  const user = auth$.user.get();
  if (!user) return;

  // Update exact match
  await supabase
    .from('promptnote_shared_tags')
    .update({ tag_path: newPath })
    .eq('tag_path', oldPath)
    .eq('owner_id', user.id);

  // Update children (tag_path starts with oldPath/)
  const { data: children } = await supabase
    .from('promptnote_shared_tags')
    .select('id, tag_path')
    .eq('owner_id', user.id)
    .like('tag_path', oldPath + '/%');

  if (children) {
    for (const child of children) {
      const updatedPath = newPath + child.tag_path.slice(oldPath.length);
      await supabase
        .from('promptnote_shared_tags')
        .update({ tag_path: updatedPath })
        .eq('id', child.id);
    }
  }

  // Refresh cache
  await refreshSharedTagsCache();
};

/**
 * Check if any tag in the path hierarchy is shared
 * Used for showing confirmation when deleting/renaming
 */
export const getSharedTagsInHierarchy = (tagPath) => {
  const owned = sharedTagPaths$.get() || [];
  return owned.filter(t =>
    t.tag_path === tagPath || t.tag_path.startsWith(tagPath + '/')
  );
};
